import { loadEnv } from "@smart/bridge-env-dom";
import { useToast } from "@smart/bridge-intake-components-dom";
import { track } from "@smart/bridge-metrics-dom";
import { appendFormLink, defaultTemplate } from "@smart/bridge-templates-basic";
import {
  FamilyProFormUri,
  extractFamilyProQuestionnaireType,
  familyProQuestionnaireFormUri,
  usIntakeTemplateId,
} from "@smart/bridge-types-basic";
import { Region } from "@smart/itops-utils-basic";
import {
  MatterSubmission,
  useDeleteSubmission,
  useLoadFamilyProTemplate,
  useLoadMatterQuestionnaireStatus,
  useLoadSetting,
  useLoadTeam,
  useSendMatterQuestionnaire,
  useSendSubmission,
  useUpdateSubmission,
} from "@smart/manage-gql-client-dom";
import {
  RoleContacts,
  useEvent,
  useSendMessage,
} from "@smart/manage-hooks-dom";

import { SubmissionActionCancel } from "./cancel";
import { SubmissionActionDelete } from "./delete";
import { SubmissionActionRemind } from "./remind";

type SubmissionActionsProps = {
  isLoadingMatterInfo?: boolean;
  matter:
    | {
        id: string;
        matterTypeId: string;
        isLead: boolean;
        reLine?: string;
      }
    | undefined;
  matterType: { name: string; id: string } | undefined;
  roleContacts: RoleContacts;
  onViewClose: () => void;
  refetch?: () => void;
} & Record<
  "cancelling" | "deleting" | "reminding",
  { selected?: MatterSubmission; onClose: () => void }
>;

export const SubmissionActions = ({
  isLoadingMatterInfo,
  matter,
  matterType,
  roleContacts,
  onViewClose,
  cancelling,
  deleting,
  reminding,
  refetch,
}: SubmissionActionsProps) => {
  const { logEvent } = useEvent();
  const { setToasts } = useToast();
  const { sendMessage } = useSendMessage();
  const setting = useLoadSetting({});
  const team = useLoadTeam({});

  const [deleteSubmission] = useDeleteSubmission();
  const [updateSubmission] = useUpdateSubmission();
  const [sendSubmission] = useSendSubmission();

  const isUsRegion = (loadEnv("Region") as Region) === "nova";

  const questionnairePartyA = useLoadMatterQuestionnaireStatus(
    {
      matterId: matter?.id || "",
      party: "a",
    },
    { skip: true },
  );
  const questionnairePartyB = useLoadMatterQuestionnaireStatus(
    {
      matterId: matter?.id || "",
      party: "b",
    },
    { skip: true },
  );
  const familyProTemplate = useLoadFamilyProTemplate(
    { templateId: usIntakeTemplateId },
    {
      skip:
        !isUsRegion ||
        reminding.selected?.formUri !== familyProQuestionnaireFormUri,
    },
  );
  const [sendQuestionnaire] = useSendMatterQuestionnaire();

  if (
    !isLoadingMatterInfo &&
    (!matter || !matterType || setting.loading || familyProTemplate.loading)
  )
    return null;

  const teamValue = team.result?.values || { name: "Firm", phone: "" };
  const isLoadingReminderData = !!isLoadingMatterInfo || setting.loading;

  const isInternalUse = (matterSubmission: MatterSubmission | undefined) =>
    matterSubmission?.status === "draft" &&
    matterSubmission?.rawSubmission.values.communicationMethod ===
      "internalUse";

  const getReminderMessageTemplate = ({
    clientMessage,
    matterSubmission,
  }: {
    clientMessage?: {
      lead?: string | null;
      matter?: string | null;
      reminder?: string | null;
    } | null;
    matterSubmission: MatterSubmission | undefined;
  }) => {
    const resendingFamilyPro =
      matterSubmission?.formUri === familyProQuestionnaireFormUri &&
      matterSubmission.status === "completed";

    const formClientMessages =
      matterSubmission?.rawSubmission?.form?.values?.clientMessages;

    if (isInternalUse(matterSubmission) || resendingFamilyPro) {
      const formMessage = formClientMessages?.new;
      const settingMessage = matter?.isLead
        ? clientMessage?.lead
        : clientMessage?.matter;
      return formMessage || settingMessage;
    }

    return formClientMessages?.reminder || clientMessage?.reminder;
  };

  return (
    <>
      <SubmissionActionCancel
        isLoadingMatterInfo={isLoadingMatterInfo}
        onConfirm={async ({ uri, formUri }) => {
          await updateSubmission({
            variables: { uri, formUri, fields: { status: "cancelled" } },
          });
          setToasts([{ text: "Form successfully closed" }]);

          track("Submission Cancelled", {
            submissionUri: uri,
            formUri,
            matterCategory: matter?.isLead ? "lead" : "matter",
            matterTypeId: matterType?.id,
            matterTypeName: matterType?.name,
          });
          refetch?.();
        }}
        onViewClose={onViewClose}
        {...cancelling}
      />
      <SubmissionActionDelete
        isLoadingMatterInfo={isLoadingMatterInfo}
        onConfirm={async ({ uri, formUri }) => {
          await deleteSubmission({ variables: { uri } });
          setToasts([{ text: "Form successfully deleted" }]);

          track("Submission Deleted", {
            submissionUri: uri,
            formUri,
            matterCategory: matter?.isLead ? "lead" : "matter",
            matterTypeId: matterType?.id,
            matterTypeName: matterType?.name,
          });
          refetch?.();
        }}
        onViewClose={onViewClose}
        {...deleting}
      />
      <SubmissionActionRemind
        isLoadingMatterInfo={isLoadingMatterInfo}
        isLoadingReminderData={isLoadingReminderData}
        matter={matter}
        matterType={matterType}
        roleContacts={roleContacts}
        team={teamValue}
        logEvent={logEvent}
        settingTemplate={
          getReminderMessageTemplate({
            matterSubmission: reminding.selected,
            clientMessage: setting.result?.defaultClientMessage,
          }) || undefined
        }
        defaultTemplate={
          getReminderMessageTemplate({
            matterSubmission: reminding.selected,
            clientMessage: defaultTemplate,
          }) || ""
        }
        familyProInviteTemplate={familyProTemplate.result?.inviteTemplate}
        schedule={setting.result?.reminder}
        onConfirm={async (
          { communicationMethod, form: supplementaryForm },
          { uri, formUri, recipient },
        ) => {
          if (!recipient) throw new Error(`Please select a recipient`);

          if (formUri === familyProQuestionnaireFormUri) {
            if (!matter) return undefined;

            if (supplementaryForm) {
              const result = await sendQuestionnaire({
                variables: {
                  matterId: matter.id,
                  clientName: recipient.contactName,
                  email: recipient.email,
                  party: "a",
                  reset: true,
                  questionnaireType: extractFamilyProQuestionnaireType(
                    supplementaryForm.uri as FamilyProFormUri,
                  ),
                },
              });
              await questionnairePartyA.refetch();
              await questionnairePartyB.refetch();

              return result.data?.sendMatterQuestionnaire.link;
            }
          } else {
            await sendSubmission({
              variables: {
                uri,
                formUri,
                communicationMethod,
                recipient: {
                  contactId: recipient.contactId,
                  contactName: recipient.contactName,
                  email: recipient.email,
                  phone: recipient.phone,
                  cell: recipient.cell,
                },
              },
              context: {
                stopOnManualSend: !!setting.result?.reminder?.stopOnManualSend,
              },
            });
          }
          track("Submission Reminded", {
            submissionUri: uri,
            formUri,
            matterCategory: matter?.isLead ? "lead" : "matter",
            matterTypeId: matterType?.id,
            matterTypeName: matterType?.name,
          });
          refetch?.();
          return undefined;
        }}
        onMessage={(
          { communicationMethod, template },
          { status, recipient },
          placeholderData,
        ) => {
          if (!recipient) throw new Error(`Please select a recipient`);
          if (communicationMethod === "internalUse") return;

          sendMessage({
            matterId: matter?.id,
            id: recipient.contactId,
            email: recipient.email,
            communicationMethod,
            placeholderData,
            subject: [matter?.reLine, "Please complete this online form"]
              .filter(Boolean)
              .join(" - "),
            template: appendFormLink(template),
            actionText:
              status === "draft" ? undefined : "Reminder successfully sent",
          });
        }}
        {...reminding}
      />
    </>
  );
};
